import _ from "lodash";

/**
 * Statische Utility-Klasse zu Zahlen und Mathematik
 */

/**
 * @param {number} decimals Anzahl der Dezimalstellen der Rundung wie 3, oder eine Nachkommastelle wie 0.001 - beides meint dasselbe
 * @returns {number} Faktor
 */
export function decimalsToFactor(decimals: number): number {
  return Number.isInteger(decimals) ? Math.pow(10, decimals) : Math.round(1 / decimals);
}

/**
 * prüft, ob zwei Zahlen gleich sind, bezogen auf ein epsilon
 */
export function equalsEpsilon(a: number, b: number, epsilon: number = 0.000001): boolean {
  return Math.abs(a - b) < epsilon;
}

/**
 * rundet eine Zahl
 * @param {number} number
 * @param {number} decimals Anzahl der Dezimalstellen der Rundung wie 3, oder eine Nachkommastelle wie 0.001 - beides meint dasselbe
 * @returns {number}
 */
export function round(number: number, decimals: number = 2): number {
  const f = decimalsToFactor(decimals);
  return Math.round(number * f) / f;
}

/**
 * Rundet eine Zahl auf die nächst kleinere Zahl ab
 * @param {number} number
 * @param {number} decimals Anzahl der Dezimalstellen der Rundung wie 3, oder eine Nachkommastelle wie 0.001 - beides meint dasselbe
 * @returns {number}
 */
export function floor(number: number, decimals: number = 2): number {
  const f = decimalsToFactor(decimals);
  return Math.floor(number * f) / f;
}

/**
 * Rundet eine Zahl auf die nächst höhere Zahl auf
 * @param {number} number
 * @param {number} decimals Anzahl der Dezimalstellen der Rundung wie 3, oder eine Nachkommastelle wie 0.001 - beides meint dasselbe
 * @returns {number}
 */
export function ceil(number: number, decimals: number = 2): number {
  const f = decimalsToFactor(decimals);
  return Math.ceil(number * f) / f;
}

/**
 * Rundet eine Zahl auf die nächst höhere ganzzahlige Potenz einer Basis auf
 * @param {number} x
 * @param {number} basis
 * @returns {number}
 */
export function ceilToNextPowerOf(x: number, basis: number = 2): number {
  return Math.pow(basis, Math.ceil(Math.log(x) / Math.log(basis)));
}

/**
 * Rundet eine Zahl auf die letzte ganzzahlige Potenz ab
 * @param {number} x
 * @param {number} power
 * @returns {number}
 */
export function floorToLastPowerOf(x: number, power: number = 2): number {
  return ceilToNextPowerOf(x, 1 / power);
}

/**
 * Rundet eine Zahl zur nächsten ganzzahligen Potenz
 * @param {number} value
 * @returns {number}
 */
export function nearestPowerOfTwo(value: number): number {
  return Math.pow(2, Math.round(Math.log(value) / Math.LN2));
}

/**
 * Begrenzt eine Zahl auf das Interval [min,max]
 * @param {number} min
 * @param {number} number
 * @param {number} max
 * @returns {number}
 */
export function clamp(min: number, number: number, max: number): number {
  return Math.min(max, Math.max(number, min));
}

export const cutBetween = clamp;

/**
 * Begrenzt number im Interval [min,max] und bildet das Ergebnis linear auf das Einheitsintervall [0,1] ab.
 * @param {number} min
 * @param {number} number
 * @param {number} max
 * @returns {number}
 */
export function mapToUnitInterval(min: number, number: number, max: number): number {
  return number < min ? 0 : number > max ? 1 : min >= max ? 0 : (number - min) / (max - min);
}

/**
 * interpoliert linear zwischen min und max
 * @param min
 * @param max
 * @param factorZeroToOne
 * @returns {number}
 */
export function lerp(min: number, max: number, factorZeroToOne: number): number {
  return max * factorZeroToOne + min * (1 - factorZeroToOne);
}

/**
 * Integriert eine Funktion f im Intervall [start,end] in Anzahl steps diskreten Schritten
 * nach der Trapezmethode.
 * @param {function} f
 * @param {number} start
 * @param {number} end
 * @param {number} steps
 * @returns {number}
 */
export function trapezoidalIntegration(
  f: (x: number) => number,
  start: number,
  end: number,
  steps: number,
): number {
  let delta = (end - start) / steps;
  let sum = f(start) + f(end);
  for (let i = 1; i < steps; i++) {
    sum += 2 * f(start + i * delta);
  }
  return (sum * delta) / 2;
}

/**
 * Testet, ob eine Zahl keine endliche, echte Zahl ist: null, undefined, NaN, infinite ergeben true.
 * @param {number} number
 * @returns number
 */
export function notFinite(number: number | null | undefined): boolean {
  return (
    number === null ||
    typeof number === "undefined" ||
    Number.isNaN(number) ||
    !Number.isFinite(number)
  );
}

/**
 * Testet, ob eine Zahl gültig und echt ist: null, undefined, NaN, infinite ergeben false.
 * @param {number} number
 * @returns boolean
 */
export function isFinite(number: number | null | undefined): boolean {
  return !notFinite(number);
}

/**
 * Cantor-Pairing erweitert für beliebig viele, auch negative ganze Zahlen.
 * Erzeugt aus n ganzen Zahlen eine neue eindeutige natürliche (positive) Zahl.
 * Somit können z.B. per Zufallszahl erzeugte IDs eindeutig zu einer neuen ID
 * kombiniert werden.
 *
 * Achtung! Die Zahlen sind nur eindeutig, wenn die Dimension des Pairings gleich ist,
 * wenn also in einem Context stets die gleiche Argument-Anzahl verwendet wird!
 *
 * Weiterhin ist zu beachten, dass die benötigte Anzahl der Bits sich ver-n-facht,
 * und Javascript-Zahlen maximal 53 Bits precision haben. Man kann also nicht beliebig
 * viele große Zahlen kombinieren, ohne in Uneindeutigkeiten zu gelangen.
 * Da die Zahlengröße mit höherer Dimmension stark wächst, ist ab 3 Zahlen mest
 * Maths.pairer die bessere Wahl.
 *
 * @param {...number} k
 * @returns {number}
 */
export function pairing(...k: number[]): number {
  if (k.length === 0) {
    return 0;
  }
  if (k.some((x) => !Number.isInteger(x))) {
    throw new Error("pairing is only unique for integers: pairing(" + k.join(", ") + ")");
  }
  const withNegatives = (x: number): number => (x < 0 ? -x * 2 + 1 : x * 2);
  let a = withNegatives(k[0]);
  for (let i = 1; i < k.length; i++) {
    const b = withNegatives(k[i]);
    const sum = a + b;
    a = (sum * (sum + 1)) / 2 + b;
  }
  if (!Number.isInteger(a)) {
    throw new Error(
      "Maths.pairing(" +
        k.join(", ") +
        ")=" +
        a +
        " is not unique because it is larger than the largest integer",
    );
  }
  return a;
}

/**
 * Erzeugt aus n Zahlen eine neue eindeutige Zahl, ähnlich wie Cantor-Pairing, aber mit definierbarem
 * Zahlenumfang der einzelnen Bereiche
 * @param {...number} sizes
 * @returns {function}
 * @example const pairer=Maths.pairer(4,3,2);
 * pairer(0,0,0)=0
 * pairer(4,0,0)=4
 * pairer(-1,0,0)=5
 * pairer(0,1,0)=9
 * pairer(-4,-3,0)=62
 * pairer(0,0,1)=63
 */
export function pairer(...sizes: number[]): (...k: number[]) => number {
  let acc = 1;
  const pow = sizes.map((s) => {
    let r = acc;
    acc = (2 * s + 1) * acc;
    return r;
  });
  if (!Number.isSafeInteger(acc)) {
    throw new Error(
      "Math.pairer(" + sizes.join(",") + ") maximum " + acc + " is not a safe integer",
    );
  }
  return (...k: number[]) => {
    if (sizes.length != k.length) {
      throw new Error("pairing: sizes.length!=k.length");
    }
    let a = 0;
    for (let i = 0; i < k.length; i++) {
      if (Math.abs(k[i]) > sizes[i]) {
        throw new Error("pairing: k[" + i + "]=" + k[i] + " > sizes[" + i + "]=" + sizes[i]);
      }
      a += (Math.abs(k[i]) + (k[i] < 0 ? sizes[i] : 0)) * pow[i];
    }
    return a;
  };
}

/**
 * Formatiert eine positive Ganzzahl in einen String zu einer bestimmten Basis
 * @param {number} number
 * @param {string} baseString
 * @return {string}
 * @example
 * Maths.formatBase(0,'ABCDEFGHIJKLMNOPQRSTUVWXYZ') => "A"
 * Maths.formatBase(23232,'ABCDEFGHIJKLMNOPQRSTUVWXYZ') => "BIJO"
 */
export function formatBase(number: number, baseString: string): string {
  const base = baseString.length;
  let result = "";
  number = Math.abs(Math.trunc(number));
  do {
    result = baseString[Math.trunc(number % base)] + result;
    number = Math.trunc(number / base);
  } while (number >= 1);
  return result;
}

/**
 * Umkehrfunktion zu formatBase
 * @param {string} text
 * @param {string} baseString
 * @returns {number}
 */
export function unformatBase(text: string, baseString: string): number {
  const base = baseString.length;
  let result = 0;
  text = text.replace(new RegExp("[^" + baseString + "]", "g"), "");
  for (let c of text) {
    result *= base;
    result += baseString.indexOf(c);
  }
  return result;
}

/**
 * Ganzzahlige Division
 * @param {number} divident
 * @param {number} divisor
 * @returns {[number,number]} [ganzzahliger quotient,rest]
 * @example
 * div(5,2)=[2,1]
 * div(-5,2)=[-2,-1]
 * div(-5,-2)=[2,-1]
 * div(5,-2)=[-2,1]
 */
export function div(divident: number, divisor: number): [number, number] {
  return [(divident - (divident % divisor)) / divisor, divident % divisor];
}

/**
 * Verteilt eine Ganzahl-Summe möglichst gleichmässig auf eine Anzahl von Array Elementen.
 * Sinnvoll z.B., um einen Geldbetrag möglichst gerecht aufzuteilen, ohne dass Centbeträge
 * durch Rundungsfehler verloren gehen. Die Aufteilung bevorzugt die ersten Elemente gegenüber
 * den letzten - die ersten n Elemente haben jeweils 1 Cent mehr als die letzten m Elemente.
 * Wenn man die Aufteilung wirklich fair machen möchte, dann müssen die Elemente anschließend
 * noch gemischt werden: _.shuffle(Maths.gleichverteilt(...))
 * @param {number} betrag der zu verteilende Betrag
 * @param {number} anzahlElemente die Anzahl der Elemente
 * @param {number} decimals die zu berücksichtigenden Nachkommastellen, also bei Euro 2 für "auf den Cent"
 * @return {number[]} Array von aufgeteilten Teilbeträgen
 * @example
 * Maths.dividedEqually(10,3,2)=[3.34, 3.33, 3.33]
 */
export function dividedEqually(
  betrag: number,
  anzahlElemente: number,
  decimals: number = 0,
): number[] {
  const f = decimalsToFactor(decimals);
  const anzahl = Math.floor(anzahlElemente);
  let [wert, rest] = div(Math.floor(betrag * f), anzahl);
  const werte = _.range(anzahl).map(() => wert);
  let i = 0;
  while (rest > 0) {
    werte[i++]++;
    rest--;
  }
  return werte.map((x) => x / f);
}

/**
 * wandelt NaN oder alles, was keine Zahl ist, zu 0. Unendlich gilt als Zahl.
 * @param {number} number
 * @return {number}
 */
export function nanToZero(number: number | null | undefined): number {
  if (typeof number != "number" || Number.isNaN(number)) {
    return 0;
  }
  return number;
}

/**
 * Ein seedbarer, schneller PRNG mit guter Qualität, nicht kryptografisch.
 * see https://stackoverflow.com/questions/521295/seeding-the-random-number-generator-in-javascript/47593316#47593316
 * @param {number} seed
 * @returns {function}
 * @example
 * // derselbe Seed führt zur exakt selben Zufallsfolge
 * const seed = 37475;
 * const prng = Maths.createPseudoRandomGeneratorMulberry32(seed);
 * console.log('1. Zufallszahl',prng());
 * console.log('2. Zufallszahl',prng());
 */
export function createPseudoRandomGeneratorMulberry32(seed: number = 1): () => number {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const _factorial: number[] = [];
/**
 * Fakultät einer Zahl, n!
 */
export function factorial(n: number): number {
  if (n == 0 || n == 1) return 1;
  if (_factorial[n] > 0) return _factorial[n];
  return (_factorial[n] = factorial(n - 1) * n);
}
